import React, { useState, useEffect, useCallback, Suspense, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Alert,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  Close,
  PictureAsPdf,
  DescriptionOutlined,
  PhotoLibraryOutlined,
  SaveOutlined,
  StraightenOutlined,
  SendOutlined,
  AssignmentOutlined,
  Compare
} from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { withDevice, WithDeviceProps } from '../hooks/withDevice';
import { Project, Photos, Homologacion, ReferenceDim } from '../types';
import { useDefaultHomologacion } from '../hooks/useDefaultHomologacion';
import { useDefaultReferenceDim } from '../hooks/useDefaultReferenceDim';
import { detectMarker, MarkerDetectionResult } from '../functions/markerDetector';
import { LoadingFullScreen } from './LoadingFullScreen';
import { captureException } from '../utils/captureException';

// Lazy load heavy components to reduce initial bundle parse time
const PhotoGallery = React.lazy(() => import('./PhotoGallery'));
const SignatureDialog = React.lazy(() => import('./SignatureDialog'));
const InformacionGeneralAccordion = React.lazy(() => import('./Accordions/AccordionInformacionGeneral'));
const ReferenceDimensionAccordion = React.lazy(() => import('./Accordions/AccordionReferenceDimension'));
const RecolectoresOlympusAccordion = React.lazy(() => import('./Accordions/AccordionRecolectoresOlympus'));
const LavacontenedoresAccordion = React.lazy(() => import('./Accordions/AccordionLavacontenedores'));
const AlumbradoLateralAccordion = React.lazy(() => import('./Accordions/AccordionAlumbradoLateral'));
const CatadioptricosLateralesAccordion = React.lazy(() => import('./Accordions/AccordionCatadioptricosLaterales'));
const CatadioptricosTraserosAccordion = React.lazy(() => import('./Accordions/AccordionCatadioptricosTraseros'));
const FrenadoIntermitenteAccordion = React.lazy(() => import('./Accordions/AccordionFrenadoIntermitente'));
const AntinieblaAccordion = React.lazy(() => import('./Accordions/AccordionAntiniebla'));
const MarchaAtrasAccordion = React.lazy(() => import('./Accordions/AccordionMarchaAtras'));
const GalibosDelanterosAccordion = React.lazy(() => import('./Accordions/AccordionGalibosDelanteros'));
const TerceraLuzFrenoAccordion = React.lazy(() => import('./Accordions/AccordionTerceraLuzFreno'));
const BandasReflectantesLateralesTraserasAccordion = React.lazy(() => import('./Accordions/AccordionBandasReflectantesLateralesTraseras'));
const BandasReflectantesTraserasFranciaAccordion = React.lazy(() => import('./Accordions/AccordionBandasReflectantesTraserasFrancia'));
const BandasReflectantesDelanterasFranciaAccordion = React.lazy(() => import('./Accordions/AccordionBandasReflectantesDelanterasFrancia'));
const ProteccionLateralDerechoAccordion = React.lazy(() => import('./Accordions/AccordionProteccionLateralDerecho'));
const ProteccionLateralIzquierdoAccordion = React.lazy(() => import('./Accordions/AccordionProteccionLateralIzquierdo'));
const SistemaAntiproyeccionAccordion = React.lazy(() => import('./Accordions/AccordionSistemaAntiproyeccion'));
const SistemaAntiempotramientoAccordion = React.lazy(() => import('./Accordions/AccordionSistemaAntiempotramiento'));
const LuzMatriculaAccordion = React.lazy(() => import('./Accordions/AccordionLuzMatricula'));
const AdhesivoPuntosCiegosAccordion = React.lazy(() => import('./Accordions/AccordionAdhesivoPuntosCiegos'));
const ProteccionDelanteraAccordion = React.lazy(() => import('./Accordions/AccordionProteccionDelantera'));
const DepositoCombustibleAddblueAccordion = React.lazy(() => import('./Accordions/AccordionDepositoCombustibleAddblue'));
const NeumaticoAccordion = React.lazy(() => import('./Accordions/AccordionNeumaticos'));
const EtiquetasVelocidadFranciaAccordion = React.lazy(() => import('./Accordions/AccordionEtiquetasVelocidadFrancia'));
const ExtintoresFranciaAccordion = React.lazy(() => import('./Accordions/AccordionExtintoresFrancia'));
const DepositosDimensionesAccordion = React.lazy(() => import('./Accordions/AccordionDepositosDimensiones'));
const CEMFranciaAccordion = React.lazy(() => import('./Accordions/AccordionCEMFrancia'));
const ChecksAccordion = React.lazy(() => import('./Accordions/AccordionChecks'));
const ResultadoAccordion = React.lazy(() => import('./Accordions/AccordionResultado'));

interface MeasurementsProps extends WithDeviceProps {
  project: Project;
  openMeasurements: boolean;
  handleClose: () => void;
  handleNextStep: (id: string) => void;
  onOpenPdf: () => void;
  setPdfType: (pdfType: string) => void;
  isSmallScreen: boolean;
  handleCloseAll: () => void;
  refetch: () => void;
}

// Move large static photo metadata outside the component so it's not recreated on each render
const helperPhotos: Photos[] = [
  { id: 'olympus-slm-photo', zone: 'olympus-slm', status: 'completed', gcs_path: '/measures/olympus-slm.jpg', gcs_thumbnail_path: '/measures/olympus-slm.jpg' },
  { id: 'lavacontenedores-photo', zone: 'lavacontenedores', status: 'completed', gcs_path: '/measures/lavacontenedores.png', gcs_thumbnail_path: '/measures/lavacontenedores.png' },
  { id: 'luces-laterales-photo', zone: 'luces-laterales', status: 'completed', gcs_path: '/measures/luces-laterales.png', gcs_thumbnail_path: '/measures/luces-laterales.png' },
  { id: 'catadioptricos-laterales-photo', zone: 'catadioptricos-laterales', status: 'completed', gcs_path: '/measures/catadioptricos-laterales.png', gcs_thumbnail_path: '/measures/catadioptricos-laterales.png' },
  { id: 'frenado-photo', zone: 'frenado', status: 'completed', gcs_path: '/measures/frenado.png', gcs_thumbnail_path: '/measures/frenado.png' },
  { id: 'frenado-angulo-photo', zone: 'frenado', status: 'completed', gcs_path: '/measures/frenado-angulo.png', gcs_thumbnail_path: '/measures/frenado-angulo.png' },
  { id: 'antiniebla-photo', zone: 'antiniebla', status: 'completed', gcs_path: '/measures/antiniebla.png', gcs_thumbnail_path: '/measures/antiniebla.png' },
  { id: 'antiniebla-angulo-photo', zone: 'antiniebla', status: 'completed', gcs_path: '/measures/antiniebla-angulo.png', gcs_thumbnail_path: '/measures/antiniebla-angulo.png' },
  { id: 'marcha-atras-photo', zone: 'marcha-atras', status: 'completed', gcs_path: '/measures/marcha-atras.png', gcs_thumbnail_path: '/measures/marcha-atras.png' },
  { id: 'marcha-atras-angulo-photo', zone: 'marcha-atras', status: 'completed', gcs_path: '/measures/marcha-atras-angulo.png', gcs_thumbnail_path: '/measures/marcha-atras-angulo.png' },
  { id: 'catadioptricos-traseros-photo', zone: 'catadioptricos-traseros', status: 'completed', gcs_path: '/measures/catadioptricos-traseros.png', gcs_thumbnail_path: '/measures/catadioptricos-traseros.png' },
  { id: 'catadioptricos-traseros-angulo-photo', zone: 'catadioptricos-traseros', status: 'completed', gcs_path: '/measures/catadioptricos-traseros-angulo.png', gcs_thumbnail_path: '/measures/catadioptricos-traseros-angulo.png' },
  { id: 'galibos-delanteros-photo', zone: 'galibos-delanteros', status: 'completed', gcs_path: '/measures/galibos-delanteros.png', gcs_thumbnail_path: '/measures/galibos-delanteros.png' },
  { id: 'tercera-luz-freno-photo', zone: 'tercera-luz-freno', status: 'completed', gcs_path: '/measures/tercera-luz-freno.png', gcs_thumbnail_path: '/measures/tercera-luz-freno.png' },
  { id: 'tercera-luz-freno-angulo-photo', zone: 'tercera-luz-freno', status: 'completed', gcs_path: '/measures/tercera-luz-freno-angulo.png', gcs_thumbnail_path: '/measures/tercera-luz-freno-angulo.png' },
  { id: 'bandas-refrectantes-laterales-photo', zone: 'bandas-refrectantes-laterales-traseras', status: 'completed', gcs_path: '/measures/bandas-refrectantes-laterales.png', gcs_thumbnail_path: '/measures/bandas-refrectantes-laterales.png' },
  { id: 'bandas-refrectantes-traseras-photo', zone: 'bandas-refrectantes-laterales-traseras', status: 'completed', gcs_path: '/measures/bandas-refrectantes-traseras.png', gcs_thumbnail_path: '/measures/bandas-refrectantes-traseras.png' },
  { id: 'bandas-refrectantes-traseras-francia-photo', zone: 'bandas-refrectantes-traseras-francia', status: 'completed', gcs_path: '/measures/bandas-refrectantes-traseras-francia.png', gcs_thumbnail_path: '/measures/bandas-refrectantes-traseras-francia.png' },
  { id: 'bandas-refrectantes-delanteras-francia-photo', zone: 'bandas-refrectantes-delanteras-francia', status: 'completed', gcs_path: '/measures/bandas-refrectantes-delanteras-francia.png', gcs_thumbnail_path: '/measures/bandas-refrectantes-delanteras-francia.png' },
  { id: 'proteccion-lateral-derecho-photo', zone: 'proteccion-lateral-derecho', status: 'completed', gcs_path: '/measures/proteccion-lateral-derecho.png', gcs_thumbnail_path: '/measures/proteccion-lateral-derecho.png' },
  { id: 'proteccion-lateral-derecho-2-photo', zone: 'proteccion-lateral-derecho', status: 'completed', gcs_path: '/measures/proteccion-lateral-derecho-2.png', gcs_thumbnail_path: '/measures/proteccion-lateral-derecho-2.png' },
  { id: 'proteccion-lateral-izquierdo-photo', zone: 'proteccion-lateral-izquierdo', status: 'completed', gcs_path: '/measures/proteccion-lateral-izquierdo.png', gcs_thumbnail_path: '/measures/proteccion-lateral-izquierdo.png' },
  { id: 'proteccion-lateral-izquierdo-2-photo', zone: 'proteccion-lateral-izquierdo', status: 'completed', gcs_path: '/measures/proteccion-lateral-izquierdo-2.png', gcs_thumbnail_path: '/measures/proteccion-lateral-izquierdo-2.png' },
  { id: 'sistema-antipoyeccion-photo', zone: 'sistema-antipoyeccion', status: 'completed', gcs_path: '/measures/sistema-antiproyeccion.png', gcs_thumbnail_path: '/measures/sistema-antiproyeccion.png' },
  { id: 'sistema-antiempotramiento-photo', zone: 'sistema-antiempotramiento', status: 'completed', gcs_path: '/measures/sistema-antiempotramiento.png', gcs_thumbnail_path: '/measures/sistema-antiempotramiento.png' },
  { id: 'luz-matricula-photo', zone: 'luz-matricula', status: 'completed', gcs_path: '/measures/luz-matricula.png', gcs_thumbnail_path: '/measures/luz-matricula.png' },
  { id: 'adhesivo-puntos-ciegos-photo', zone: 'adhesivo-puntos-ciegos', status: 'completed', gcs_path: '/measures/adhesivo-puntos-ciegos.png', gcs_thumbnail_path: '/measures/adhesivo-puntos-ciegos.png' },
  { id: 'proteccion-delantera-photo', zone: 'proteccion-delantera', status: 'completed', gcs_path: '/measures/proteccion-delantera.png', gcs_thumbnail_path: '/measures/proteccion-delantera.png' },
  { id: 'deposito-combustible-addblue-photo', zone: 'deposito-combustible-addblue', status: 'completed', gcs_path: '/measures/deposito-combustible-addblue.png', gcs_thumbnail_path: '/measures/deposito-combustible-addblue.png' },
  { id: 'deposito-combustible-photo', zone: 'deposito-combustible', status: 'completed', gcs_path: '/measures/deposito-combustible.png', gcs_thumbnail_path: '/measures/deposito-combustible.png' },
  { id: 'dimensiones-referencia-photo', zone: 'reference-dimencion', status: 'completed', gcs_path: '/measures/reference-dimensions.png', gcs_thumbnail_path: '/measures/reference-dimensions.png' },
  { id: 'dimensiones-referencia-2-photo', zone: 'reference-dimencion', status: 'completed', gcs_path: '/measures/reference-dimensions-side.png', gcs_thumbnail_path: '/measures/reference-dimensions-side.png' },
];

// Wrapper component to track when accordions are loaded
interface AccordionLoaderProps {
  accordionId: string;
  onLoaded: (id: string) => void;
  children: React.ReactNode;
}

const AccordionLoader: React.FC<AccordionLoaderProps> = ({ accordionId, onLoaded, children }) => {
  const hasReportedRef = useRef(false);
  
  useEffect(() => {
    if (!hasReportedRef.current) {
      hasReportedRef.current = true;
      onLoaded(accordionId);
    }
  }, [accordionId, onLoaded]);
  
  return <>{children}</>;
};

// Add this component before the Identification component (around line 61)
const NoTransition = React.forwardRef<any, any>(
  function NoTransition(props, ref) {
    const {
      children,
      appear,
      in: inProp,
      timeout,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      ownerState,
      ...rest
    } = props;
    // Return children directly without wrapper to preserve Dialog's layout
    return <>{children}</>;
  }
);

const Measurements: React.FC<MeasurementsProps> = ({
  project,
  openMeasurements,
  handleClose,
  handleNextStep,
  onOpenPdf,
  isSmallScreen,
  setPdfType,
  handleCloseAll,
  refetch
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [measure, setMeasure] = useState<boolean>(false);
  const [saveAndMeasureRef, setsaveAndMeasureRef] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedHelper, setSelectedHelper] = useState<number>(0);
  const [selectedPhotos, setSelectedPhotos] = useState<Photos[]>([]);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState<boolean>(false);
  const [allAccordionsLoaded, setAllAccordionsLoaded] = useState<boolean>(false);
  const [loadingAccordions, setLoadingAccordions] = useState<boolean>(true);

  const { defaultHomologacion, loading: loadingHomologacion, error: homologacionError } = useDefaultHomologacion();
  const { defaultReferenceDim, loading: loadingReferenceDim, error: referenceDimError } = useDefaultReferenceDim();
  
  const [homologacion, setHomologacion] = useState<Homologacion | undefined>(undefined);
  const [referenceDim, setReferenceDim] = useState<ReferenceDim | undefined>(undefined);

  // Cache estático para códigos comunes - se calcula una vez y se reutiliza
  const [commonCodesCache, setCommonCodesCache] = useState<Set<string>>(new Set());
  const [sourceItemsCache, setSourceItemsCache] = useState<Map<string, any>>(new Map());
  const [targetItemsCache, setTargetItemsCache] = useState<Map<string, any>>(new Map());
  
  // Referencias para detectar cambios específicos y optimización
  const previousValuesRef = useRef<Map<string, any>>(new Map());
  const syncTimeoutHomToRef = useRef<NodeJS.Timeout | null>(null);
  const syncTimeoutRefToHom = useRef<NodeJS.Timeout | null>(null);
  const prevHomologacionRef = useRef<any>(null);
  const prevReferenceDimRef = useRef<any>(null);
  
  // Debouncing para escritura fluida
  const inputDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSyncRef = useRef<{ source: any, target: any, field: string, targetSetter: any, targetField: string } | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [autoSaveDirty, setAutoSaveDirty] = useState(false);
  const [firstDirtyAt, setFirstDirtyAt] = useState<number | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isEditable = project?.editable !== false;
  
  // Tracking accordion loading
  const loadedAccordionsRef = useRef<Set<string>>(new Set());
  const expectedAccordionsCountRef = useRef<number>(0);

  // Callback to handle accordion loaded
  const handleAccordionLoaded = useCallback((accordionId: string) => {
    if (!loadedAccordionsRef.current.has(accordionId)) {
      loadedAccordionsRef.current.add(accordionId);
      
      // Check if all expected accordions are loaded
      if (expectedAccordionsCountRef.current > 0 && 
          loadedAccordionsRef.current.size >= expectedAccordionsCountRef.current) {
        setAllAccordionsLoaded(true);
      }
    }
  }, []);

  // Calculate expected accordion count based on project/model 29 in total
  useEffect(() => {
    if (!project || !openMeasurements) {
      setAllAccordionsLoaded(false);
      loadedAccordionsRef.current.clear();
      expectedAccordionsCountRef.current = 0;
      return;
    }
    
    // 28 accordions are always rendered. 1 conditional accordion renders
    // only if the model is Olympus, SLM or Lavacontenedores.
    const modeloCarrozeria = (project['DETALLES EQUIPO'] as any)?.['Modelo']?.value || '';
    const hasConditionalAccordion = ['Olympus', 'SLM', 'Lavacontenedores'].includes(modeloCarrozeria);
    const expectedCount = 28 + (hasConditionalAccordion ? 1 : 0);
    
    expectedAccordionsCountRef.current = expectedCount;
    loadedAccordionsRef.current.clear();

    // Use a small timeout to check if accordions are already loaded (cached)
    // This handles the case where lazy components are already in React's cache
    const checkTimeout = setTimeout(() => {
      if (expectedAccordionsCountRef.current > 0 && 
          loadedAccordionsRef.current.size >= expectedAccordionsCountRef.current) {
        setAllAccordionsLoaded(true);
      }
    }, 100);
    
    return () => clearTimeout(checkTimeout);
  }, [project, openMeasurements]);

  useEffect(() => {
    if (allAccordionsLoaded) {
      setTimeout(() => {
        setLoadingAccordions(false);
      }, 400);
    }
  }, [allAccordionsLoaded]);

  // Función optimizada para shallow clone cuando sea posible
  const optimizedClone = useCallback((obj: any, deep: boolean = false): any => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
      return deep ? obj.map(item => optimizedClone(item, true)) : [...obj];
    }
    return deep ? JSON.parse(JSON.stringify(obj)) : { ...obj };
  }, []);

  // Función ultra-optimizada para obtener todos los códigos de un objeto
  const getAllCodes = useCallback((obj: any): Set<string> => {
    const codes = new Set<string>();
    const traverse = (current: any) => {
      if (Array.isArray(current)) {
        for (let i = 0; i < current.length; i++) {
          const item = current[i];
          if (item?.code) codes.add(item.code);
          if (typeof item === 'object' && item !== null) traverse(item);
        }
      } else if (current && typeof current === 'object') {
        for (const value of Object.values(current)) {
          traverse(value);
        }
      }
    };
    traverse(obj);
    return codes;
  }, []);

  // Función para construir caches una sola vez (optimizada)
  const buildStaticCaches = useCallback((homologacionData: any, referenceDimData: any) => {
    if (!homologacionData || !referenceDimData) return;

    // Obtener códigos de ambos objetos
    const homologacionCodes = getAllCodes(homologacionData);
    const referenceDimCodes = getAllCodes(referenceDimData);
    
    // Crear intersección (más eficiente)
    const commonCodes = new Set<string>();
    if (homologacionCodes.size < referenceDimCodes.size) {
      homologacionCodes.forEach(code => referenceDimCodes.has(code) && commonCodes.add(code));
    } else {
      referenceDimCodes.forEach(code => homologacionCodes.has(code) && commonCodes.add(code));
    }

    // Construir caches de items (optimizado)
    const sourceCache = new Map<string, any>();
    const targetCache = new Map<string, any>();
    
    const buildItemCache = (obj: any, cache: Map<string, any>) => {
      const traverse = (current: any) => {
        if (Array.isArray(current)) {
          for (const item of current) {
            if (item?.code && commonCodes.has(item.code)) {
              cache.set(item.code, item);
            }
            if (typeof item === 'object' && item !== null) traverse(item);
          }
        } else if (current && typeof current === 'object') {
          for (const value of Object.values(current)) {
            traverse(value);
          }
        }
      };
      traverse(obj);
    };

    buildItemCache(homologacionData, sourceCache);
    buildItemCache(referenceDimData, targetCache);

    setCommonCodesCache(commonCodes);
    setSourceItemsCache(sourceCache);
    setTargetItemsCache(targetCache);
  }, [getAllCodes]);

  // Effect de limpieza mejorado para prevenir memory leaks
  useEffect(() => {
    return () => {
      // Limpiar todos los timeouts
      if (syncTimeoutHomToRef.current) {
        if (typeof syncTimeoutHomToRef.current === 'number') {
          clearTimeout(syncTimeoutHomToRef.current);
        } else {
          clearTimeout(syncTimeoutHomToRef.current as NodeJS.Timeout);
        }
      }
      if (syncTimeoutRefToHom.current) {
        if (typeof syncTimeoutRefToHom.current === 'number') {
          clearTimeout(syncTimeoutRefToHom.current);
        } else {
          clearTimeout(syncTimeoutRefToHom.current as NodeJS.Timeout);
        }
      }
      if (inputDebounceRef.current) {
        clearTimeout(inputDebounceRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Limpiar referencias
      pendingSyncRef.current = null;
      setIsTyping(false);
    };
  }, []);

  // Efecto para recalcular caches cuando cambien los objetos principales
  useEffect(() => {
    if (homologacion && referenceDim) {
      buildStaticCaches(homologacion, referenceDim);
    }
  }, [homologacion, referenceDim, buildStaticCaches]);

  useEffect(() => {
    if (project && defaultHomologacion) {
      // Use existing homologacion or default
      const homologacionToUse = project.homologacion || { ...defaultHomologacion };
      setHomologacion(homologacionToUse);
    }
  }, [project, defaultHomologacion]);

  useEffect(() => {
    if (project && defaultReferenceDim) {
      const referenceDimToUse = project.reference_dimensions || { ...defaultReferenceDim };
      setReferenceDim(referenceDimToUse);
    }
  }, [project, defaultReferenceDim]);

  useEffect(() => {
    if (project) {
      if (project.homologacion) {
        setHomologacion({ ...project.homologacion });
      }
      if (project.reference_dimensions) {
        setReferenceDim({ ...project.reference_dimensions });
      }
    }
  }, [project?.homologacion, project?.reference_dimensions, project?.homologation_revision]);

  useEffect(() => {
    if (homologacionError) {
      console.warn('Homologacion fetch error (using local fallback):', homologacionError);
    }
  }, [homologacionError]);


  const handleHomologacionChange = useCallback((updatedData: Partial<Homologacion>) => {
    setHomologacion(prev => {
      if (!prev) return prev;
      const newHomologacion = { ...prev, ...updatedData };
      return newHomologacion;
    });
  }, []);

  const handleReferenceDimChange = useCallback((updatedData: Partial<ReferenceDim>) => {
    setReferenceDim(prev => {
      if (!prev) return prev;
      const newReferenceDim = { ...prev, ...updatedData };
      return newReferenceDim;
    });
  }, []);

  // Función ultra-optimizada para sincronizar usando caches estáticos
  const syncValuesBetweenObjects = useCallback((sourceObject: any, targetObject: any, sourceField: string, targetField: string) => {
    // Si no hay códigos comunes, salir inmediatamente
    if (commonCodesCache.size === 0) {
      return targetObject;
    }

    const updatedTarget = JSON.parse(JSON.stringify(targetObject)); // Deep clone
    
    // Actualizar caches con datos actuales (muy rápido)
    const updateCache = (obj: any, cache: Map<string, any>) => {
      const traverse = (current: any) => {
        if (Array.isArray(current)) {
          current.forEach(item => {
            if (item?.code && cache.has(item.code)) {
              cache.set(item.code, item);
            }
            traverse(item);
          });
        } else if (current && typeof current === 'object') {
          Object.values(current).forEach(value => traverse(value));
        }
      };
      traverse(obj);
    };

    // Actualizar cache del target con el objeto clonado
    const currentTargetCache = new Map(targetItemsCache);
    updateCache(updatedTarget, currentTargetCache);

    // Actualizar cache del source con datos actuales
    const currentSourceCache = new Map(sourceItemsCache);
    updateCache(sourceObject, currentSourceCache);
    
    // Sincronización ultra-rápida usando solo códigos comunes
    let syncCount = 0;
    commonCodesCache.forEach(code => {
      const sourceItem = currentSourceCache.get(code);
      const targetItem = currentTargetCache.get(code);
      
      if (sourceItem && targetItem && 
          sourceItem[sourceField] !== null && 
          sourceItem[sourceField] !== undefined) {
        const oldValue = targetItem[targetField];
        targetItem[targetField] = sourceItem[sourceField];
        syncCount++;
        
      }
    });
    return updatedTarget;
  }, [commonCodesCache, sourceItemsCache, targetItemsCache]);

  // Función auxiliar para encontrar valor por código (ultra-optimizada)
  const findValueByCode = useCallback((obj: any, code: string, field: string): any => {
    const traverse = (current: any): any => {
      if (Array.isArray(current)) {
        for (let i = 0; i < current.length; i++) {
          const item = current[i];
          if (item?.code === code) return item[field];
          if (typeof item === 'object' && item !== null) {
            const result = traverse(item);
            if (result !== undefined) return result;
          }
        }
      } else if (current && typeof current === 'object') {
        for (const value of Object.values(current)) {
          const result = traverse(value);
          if (result !== undefined) return result;
        }
      }
      return undefined;
    };
    return traverse(obj);
  }, []);

  // Optimized function to update nested object immutably without deep cloning
  const updateNestedValue = useCallback((obj: any, path: string[], value: any): any => {
    if (path.length === 0) return value;
    
    const [key, ...rest] = path;
    
    if (Array.isArray(obj)) {
      const index = parseInt(key, 10);
      if (isNaN(index)) return obj;
      
      const newArray = [...obj];
      if (index >= 0 && index < newArray.length) {
        newArray[index] = updateNestedValue(newArray[index], rest, value);
      }
      return newArray;
    }
    
    if (obj && typeof obj === 'object') {
      return {
        ...obj,
        [key]: updateNestedValue(obj[key], rest, value),
      };
    }
    
    return obj;
  }, []);

  // Optimized function to find path to item by code (cached paths)
  const codePathCache = useRef<Map<string, string[]>>(new Map());
  
  const findPathByCode = useCallback((obj: any, code: string, cacheContext: string, currentPath: string[] = []): string[] | null => {
    // Check cache first
    const cacheKey = `${cacheContext}-${JSON.stringify(currentPath)}-${code}`;
    if (codePathCache.current.has(cacheKey)) {
      return codePathCache.current.get(cacheKey) || null;
    }

    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        const item = obj[i];
        if (item?.code === code) {
          const path = [...currentPath, String(i)];
          codePathCache.current.set(cacheKey, path);
          return path;
        }
        if (typeof item === 'object' && item !== null) {
          const result = findPathByCode(item, code, cacheContext, [...currentPath, String(i)]);
          if (result) {
            codePathCache.current.set(cacheKey, result);
            return result;
          }
        }
      }
    } else if (obj && typeof obj === 'object') {
      for (const [key, value] of Object.entries(obj)) {
        if (value && typeof value === 'object') {
          const result = findPathByCode(value, code, cacheContext, [...currentPath, key]);
          if (result) {
            codePathCache.current.set(cacheKey, result);
            return result;
          }
        }
      }
    }
    
    return null;
  }, []);

  // Función para detectar cambio específico y sincronizar solo ese código (ultra-optimizada)
  const detectAndSyncSpecificChange = useCallback((
    sourceNewData: any,
    sourceOldData: any,
    sourceField: string,
    targetData: any,
    targetSetter: (updater: (prev: any) => any) => void,
    targetField: string,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
    targetCacheContext: string,
    delay: number = 300
  ) => {
    if (timeoutRef.current) {
      if (typeof timeoutRef.current === 'number') {
        clearTimeout(timeoutRef.current);
      } else {
        clearTimeout(timeoutRef.current as NodeJS.Timeout);
      }
    }

    // Debounce sync operation to avoid running on every keystroke
    timeoutRef.current = setTimeout(() => {
      if (!sourceNewData || !sourceOldData || !targetData || commonCodesCache.size === 0 ) {
        return;
      }

      // Detección rápida: solo verificar códigos comunes usando cache
      const changedCodes: Array<{ code: string, newValue: any }> = [];
      
      commonCodesCache.forEach(code => {
        const newValue = findValueByCode(sourceNewData, code, sourceField);
        const oldValue = findValueByCode(sourceOldData, code, sourceField);

        if (newValue !== oldValue && newValue !== null && newValue !== undefined) {
          changedCodes.push({ code, newValue });
        }
      });

      if (changedCodes.length === 0) return;

      // Sincronización optimizada: resolver paths con el estado más reciente del target
      targetSetter(currentTarget => {
        if (!currentTarget) return currentTarget;
        
        let updatedTarget = currentTarget;
        let hasChanges = false;

        changedCodes.forEach(({ code, newValue }) => {
          const path = findPathByCode(currentTarget, code, targetCacheContext);
          if (!path) return;
          
          // Obtener el valor actual en la ruta
          let currentValue: any = currentTarget;
          for (const key of path) {
            if (currentValue == null) break;
            const index = parseInt(key, 10);
            if (!isNaN(index)) {
              currentValue = Array.isArray(currentValue) ? currentValue[index] : undefined;
            } else {
              currentValue = currentValue[key];
            }
          }

          if (currentValue && currentValue[targetField] !== newValue) {
            const fullPath = [...path, targetField];
            updatedTarget = updateNestedValue(updatedTarget, fullPath, newValue);
            hasChanges = true;
          }
        });

        return hasChanges ? updatedTarget : currentTarget;
      });
    }, 900); // 500ms debounce for sync operations
  }, [commonCodesCache, findValueByCode, findPathByCode, updateNestedValue]);

  // Función optimizada para manejar cambios en homologacion con escritura fluida
  const handleHomologacionChangeWithSync = useCallback((updatedData: Partial<Homologacion>) => {
    if (!homologacion) return;

    const prevSnapshot = optimizedClone(homologacion, true);
    prevHomologacionRef.current = prevSnapshot;

    const newHomologacion = { ...homologacion, ...updatedData };

    setHomologacion(newHomologacion);
    setAutoSaveDirty(true);
    if (!firstDirtyAt) setFirstDirtyAt(Date.now());
    setIsTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1500);

    if (referenceDim) {
      detectAndSyncSpecificChange(
        newHomologacion,
        prevSnapshot,           // 👈 estado viejo REAL
        'value_human',
        referenceDim,
        setReferenceDim,
        'value',
        syncTimeoutHomToRef,
        'referenceDim'
      );
    }
  }, [homologacion, referenceDim, detectAndSyncSpecificChange, firstDirtyAt]);


  // Función optimizada para manejar cambios en referenceDim con escritura fluida
  const handleReferenceDimChangeWithSync = useCallback((updatedData: Partial<ReferenceDim>) => {
    if (!referenceDim) return;

    const prevSnapshot = optimizedClone(referenceDim, true);
    prevReferenceDimRef.current = prevSnapshot;

    const newReferenceDim = { ...referenceDim, ...updatedData };

    setReferenceDim(newReferenceDim);
    setAutoSaveDirty(true);
    if (!firstDirtyAt) setFirstDirtyAt(Date.now());
    setIsTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1500);

    if (homologacion) {
      detectAndSyncSpecificChange(
        newReferenceDim,
        prevSnapshot,          // 👈 estado viejo REAL
        'value',
        homologacion,
        setHomologacion,
        'value_human',
        syncTimeoutRefToHom,
        'homologacion'
      );
    }
  }, [referenceDim, homologacion, detectAndSyncSpecificChange, firstDirtyAt]);


  // Memoizar las funciones de cambio para evitar re-renders de los accordion children
  const memoizedHomologacionChange = useMemo(
    () => handleHomologacionChangeWithSync,
    [handleHomologacionChangeWithSync]
  );

  const memoizedReferenceDimChange = useMemo(
    () => handleReferenceDimChangeWithSync,
    [handleReferenceDimChangeWithSync]
  );

  // Function to reload project data and sync with local states
  const reloadProjectData = useCallback(async () => {
    try {
      console.log('🔄 Reloading project data...');
      await refetch();
      
      // Force update states after refetch - this will be caught by useEffect
      // but we can also do an immediate check if needed
      console.log('✅ Project data reloaded successfully');
    } catch (error) {
      console.error('❌ Error reloading project data:', error);
      setError('Failed to reload project data');
    }
  }, [refetch]);

  const getPhotosForGallery = useCallback((indexOrIndices: number | number[]) => {
    if (Array.isArray(indexOrIndices)) {
      return indexOrIndices.map(index => helperPhotos[index]).filter(Boolean);
    }
    return [helperPhotos[indexOrIndices]];
  }, []); // stable, helperPhotos is static

  const handleImageIconClick = useCallback((e: React.MouseEvent, indexOrIndices: number | number[]) => {
    e.stopPropagation();
    setGalleryOpen(true);
    
    if (Array.isArray(indexOrIndices)) {
      const photos = getPhotosForGallery(indexOrIndices);
      setSelectedPhotos(photos);
      setSelectedHelper(-1);
    } else {
      setSelectedHelper(indexOrIndices);
      setSelectedPhotos([]);
    }
  }, [getPhotosForGallery]);

  const handleSketchImageIconClick = useCallback(
    (e: React.MouseEvent, urls: string[] | undefined) => {
    e.stopPropagation();
    setGalleryOpen(true);
    
    if (Array.isArray(urls)) {
      const photos = urls.map(
        (url, idx) => (
          { id: `${idx}-photo`, zone: 'measurements', status: 'completed', gcs_path: url, gcs_thumbnail_path: url }
        )
      );
      setSelectedPhotos(photos);
      setSelectedHelper(-1);
    }
  }, []);

  // Function to remove gcs_sketch_images_path keys from homologacion
  const filterHomologacionForSave = useCallback(
    (homologacionData: Homologacion | undefined): Homologacion => {
    const filtered = JSON.parse(JSON.stringify(homologacionData)); // Deep clone
    
    // Function to recursively remove gcs_sketch_images_path keys
    const removeGcsSketchKeys = (obj: any): any => {
      if (obj && typeof obj === 'object') {
        if (Array.isArray(obj)) {
          return obj.map(removeGcsSketchKeys);
        } else {
          const newObj: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (key !== 'gcs_sketch_images_path') {
              newObj[key] = removeGcsSketchKeys(value);
            }
          }
          return newObj;
        }
      }
      return obj;
    };

    return removeGcsSketchKeys(filtered);
  }, []);

  const sanitizeUndefinedToNull = useCallback((value: any): any => {
    if (value === undefined) return null;
    if (Array.isArray(value)) return value.map(sanitizeUndefinedToNull);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, sanitizeUndefinedToNull(v)])
      );
    }
    return value;
  }, []);

  const handleSaveHomologacion = useCallback(async () => {
    if (!project?.project_code || !homologacion) {
      setError(t('errors.noDataToSave'));
      return;
    }
    
    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError('No idToken found');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Filter out gcs_sketch_images_path keys before saving
      const filteredHomologacion = filterHomologacionForSave(homologacion);

      const updateData = {
        homologacion: filteredHomologacion,
        reference_dimensions: referenceDim,
        status_mediciones: 'progress',
      };

      const sanitizedUpdateData = sanitizeUndefinedToNull(updateData);

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, 
        sanitizedUpdateData, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );
      console.log('✅ Homologación saved successfully:', response);
      await reloadProjectData();
      setAutoSaveDirty(false);
      setFirstDirtyAt(null);
    } catch (error: any) {
      captureException(error, { tags: { feature: 'measurements' }, extra: { action: 'saveHomologacion', project_code: project?.project_code } });
      console.error('❌ Error saving homologación:', error);
      setError(error.response?.data?.error || t('capture.updateFailed'));
    } finally {
      setSaving(false);
    }
  }, [project, homologacion, referenceDim, t, user, filterHomologacionForSave, reloadProjectData]);

  const handleReprocess = useCallback(async () => {
    if (!project?.project_code || !homologacion || !project?.fotos) {
      setError(t('errors.noDataToSave'));
      return;
    }
    if (!user) throw new Error(t('errors.processing.userNotAuthenticated'));
    const idToken = await user.getIdToken();

    try {
      setMeasure(true);
      setError(null);
      console.log('🔍 Measurmenst Reprocess - generating :', generating);

      const filterPhotos = project.fotos.filter(
        (foto: Photos) => [
          'Delantera', 'Trasera', 'Izquierda', 'Derecha'
        ].includes(foto?.zone || '')
      );
      console.log('Fotos filtradas:', filterPhotos);
      await Promise.all(filterPhotos.map(async (photo) => {
        let markerResult: MarkerDetectionResult | null = null;
        const photoPath = photo.gcs_path;

        const response = await fetch(photoPath);
        if (!response.ok) {
          throw new Error(`Failed to download image: ${response.statusText}`);
        }
        const blob = await response.blob();
        const file = new File([blob], `photo-${photo.id}.jpg`, { type: blob.type || 'image/jpeg' });
        
        try {
          markerResult = await detectMarker(file);
          if (markerResult?.corners) {
            const referencePoints = {
              imageInfo: {
                height: markerResult.height,
                width: markerResult.width,
                size_reference_mm: 200,
              },
              points: markerResult.corners,
              userAgent: navigator.userAgent,
            };
            photo.reference_points = referencePoints;
          } else {
            console.warn(`No marker detected for photo ${photo.zone}`);
          }
        } catch (error: any) {
          captureException(error, { tags: { feature: 'measurements' }, extra: { action: 'detectMarker', zone: photo.zone, project_code: project?.project_code } });
          console.error('❌ Error detecting marker for', photo.zone, ':', error);
        }
      }))
      // Make the API call and wait for completion
      const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/recalculate/measures`, 
        {
          project_code: project.project_code,
          fotos: filterPhotos
        }, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });

      console.log("response", response);
      await reloadProjectData();      
    } catch (error: any) {
      captureException(error, { tags: { feature: 'measurements' }, extra: { action: 'reprocessHomologacion', project_code: project?.project_code } });
      console.error('❌ Error reprocessing homologación:', error);
      setError(error.response?.data?.error || t('capture.updateFailed'));
    } finally {
      setMeasure(false);
    }
  }, [project, homologacion, t, user]);


  const handleSaveAndMeasureReferences = useCallback(async () => {
    if (!project?.project_code || !referenceDim) {
      setError(t('errors.noDataToSave'));
      return;
    }
    
    const idToken = await user?.getIdToken();
    if (!idToken) {
      setError('No idToken found');
      return;
    }

    try {
      setsaveAndMeasureRef(true);
      setError(null);
      
      const updateData = {
        reference_dimensions: referenceDim
      };
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, 
        updateData, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );
      
      console.log('✅ Reference Dimensions saved successfully:', response);

      const response_measure = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/recalculate/height`, 
        {
          project_code: project.project_code
        }, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });

      console.log("Response: ", response_measure);
      await reloadProjectData();
    } catch (error: any) {
      captureException(error, { tags: { feature: 'measurements' }, extra: { action: 'saveReferenceDimensions', project_code: project?.project_code } });
      console.error('❌ Error saving Reference Dimensions:', error);
      setError(error.response?.data?.error || t('capture.updateFailed'));
    } finally {
      console.log('✅ Setting saveAndMeasureRef to false');
      setsaveAndMeasureRef(false);
    }
  }, [project, referenceDim, t, user, reloadProjectData]);
  
  const handleGenerateAndSend = useCallback(() => {
    if (!project?.project_code || !homologacion || !project?.fotos) {
      setError(t('errors.noDataToSave'));
      return;
    }
    // Set generating to true when opening signature dialog
    setGenerating(true);
    setSignatureDialogOpen(true);
  }, [project, homologacion, t]);

  const handleSignatureSave = useCallback(async (signatureDataUrl: string) => {
    if (!project?.project_code || !user) {
      setError(t('errors.processing.userNotAuthenticated'));
      return;
    }

    if (!project?.project_code || !homologacion) {
      setError(t('noDataToSave'));
      return;
    }

    const idToken = await user.getIdToken();
    if (!idToken) {
      setError(t('errors.processing.noIdToken', 'No se encontró token de autenticación'));
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setSignatureDialogOpen(false);

      // Convert data URL to blob
      const response = await fetch(signatureDataUrl);
      const blob = await response.blob();
      
      // Create FormData to send signature image
      const formData = new FormData();
      formData.append('signature', blob, 'signature.png');
      
      // Calculate version
      const version = (project.homologation_revision || 0) + 1;

      // Upload signature and update project
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}/signature?version=${version}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log('✅ Signature uploaded successfully:', uploadResponse);

      // Filter out gcs_sketch_images_path keys before saving
      const filteredHomologacion = filterHomologacionForSave(homologacion);

      // Update project status
      const updateHomRefData = {
        homologacion: filteredHomologacion,
        reference_dimensions: referenceDim,
      };
      const updateHomRefResponse = await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, 
        updateHomRefData, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );
      console.log('✅ Homologation and Reference Dim successfully saved:', updateHomRefResponse);

      // Generate PDF
      const homologationPdfResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/generate/homologation-pdf`, 
        {
          project_code: project.project_code,
          version: version,
        }, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );

      const updateData = {
        status_mediciones: 'completed',
        homologation_verified_by: user?.uid,
        homologation_revision: version,
      };
      const updateResponse = await axios.put(
        `${process.env.REACT_APP_API_URL || ''}/api/project/${project.project_code}`, 
        updateData, 
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        }
      );

      console.log('✅ Homologation PDF generated successfully:', homologationPdfResponse);
      await reloadProjectData();
    } catch (error: any) {
      captureException(error, { tags: { feature: 'measurements' }, extra: { action: 'saveHomologacionWithSignature', project_code: project?.project_code } });
      console.error('❌ Error saving homologación with signature:', error);
      setError(error.response?.data?.error || t('capture.updateFailed'));
    } finally {
      setGenerating(false);
    }
  }, [project, homologacion, referenceDim, t, user, reloadProjectData, filterHomologacionForSave]);


  // Auto-guardado: 120s desde la primera edición pendiente, sin desplazar por ediciones posteriores
  useEffect(() => {
    if (!openMeasurements) return;
    const interval = setInterval(() => {
      if (!autoSaveDirty) return;
      if (!firstDirtyAt) return;
      if (isTyping || saving || generating || measure) return;
      const elapsed = Date.now() - firstDirtyAt;
      if (elapsed >= 120000) {
        handleSaveHomologacion();
      }
    }, 10000); // comprobación cada 10s
    return () => clearInterval(interval);
  }, [openMeasurements, autoSaveDirty, firstDirtyAt, isTyping, saving, generating, measure, handleSaveHomologacion]);

  return (
    <Dialog
      fullScreen
      open={openMeasurements && project !== null}
      keepMounted
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        pt: isSmallScreen ? "56px" : "64px",
        pb: "56px",
      }}
      onClose={handleClose}
      slots={{ transition: NoTransition }}
      key={"measurements"}
    >
      <DialogTitle sx={{ p: 0, bgcolor: 'background.light' }}>
        <AppBar
          color="inherit"
          position="fixed"
          sx={{
            // zIndex: (theme) => theme.zIndex.drawer,
            pt: isSmallScreen ? "56px" : "64px",
          }}
        >
          <Toolbar sx={{ p: 1 }}>
            <IconButton onClick={handleClose} color="inherit" sx={{ mr: 1 }}><ArrowBack/></IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }} />
            <Tooltip title={t('common.save')} placement="bottom">
              <IconButton 
                onClick={handleSaveHomologacion}
                disabled={saving || generating || measure || !isEditable}
                color='primary'  
                sx={{ mr: 0.5 }}
              >
                {saving ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  <SaveOutlined />
                )}
              </IconButton>
            </Tooltip>
            {process.env.NODE_ENV === 'test' ? (
              <Tooltip title={t('measurments.redoMeasurments')} placement="bottom">
                <IconButton 
                  onClick={handleReprocess}
                  disabled={measure || generating || saving || !isEditable}
                  color='primary'  
                  sx={{ mr: 0.5 }}
                >
                  {measure ? (
                    <CircularProgress size={24} color="primary" />
                  ) : (
                    <StraightenOutlined />
                  )}
                </IconButton>
              </Tooltip>
            ) : null}
            {project?.gcs_series_book_path && (
              <Tooltip title={t('projectList.seriesBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('series');
                }}
                sx={{ mr: 0.5 }}><Compare /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_homologation_book_path && (
              <Tooltip title={t('projectList.homologationBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('homologation');
                }}
                sx={{ mr: 0.5 }}><AssignmentOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_image_book_path && (
              <Tooltip title={t('projectList.imageBookTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('image_book');
                }}
                sx={{ mr: 0.5 }}><PhotoLibraryOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_report_path && (
              <Tooltip title={t('projectList.reportTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('report');
                }}
                sx={{ mr: 0.5 }}><DescriptionOutlined /></IconButton>
              </Tooltip>
            )}
            {project?.gcs_pdf_path && (
              <Tooltip title={t('projectList.launchTitle', { code: project?.project_code || '' })} placement="bottom">
                <IconButton onClick={() => {
                  onOpenPdf();
                  setPdfType('lanzamiento');
                }}
                sx={{ mr: 0.5 }}><PictureAsPdf /></IconButton>
              </Tooltip>
            )}
            <IconButton onClick={handleCloseAll} color="inherit"><Close /></IconButton>
          </Toolbar>
        </AppBar>
        {loadingAccordions && (
          <LoadingFullScreen isSmallScreen={isSmallScreen} series={project?.gcs_series_book_path} homologation={project?.gcs_homologation_book_path} image={project?.gcs_image_book_path} report={project?.gcs_report_path} lanzamiento={project?.gcs_pdf_path} />
        )}
        {project && (
          <Box sx={{ py: 3, pl: 2, pr: 2, minHeight: "calc(100vh - 112px - " + (isSmallScreen ? "56px" : "72px") + ")", mt: isSmallScreen ? "56px" : "64px" }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="informacion-general" onLoaded={handleAccordionLoaded}>
                  <InformacionGeneralAccordion
                    project={project}
                    homologacion={homologacion}
                    onHomologacionChange={memoizedHomologacionChange}
                  />
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
              <AccordionLoader accordionId="reference-dimension" onLoaded={handleAccordionLoaded}>
                <ReferenceDimensionAccordion
                  onImageIconClick={handleImageIconClick}
                  reference_dimension={referenceDim}
                  onReferenceDimChange={memoizedReferenceDimChange}
                  handleSaveAndMeasureReferences={handleSaveAndMeasureReferences}
                  disable={saveAndMeasureRef}
                  otherDisable={generating || measure || saving || !isEditable}
                  editable={isEditable}
                />
             </AccordionLoader>
              </Suspense>

              {(() => {
                const modeloCarrozeria = homologacion?.informacion_general?.modelo 
                  || project['DETALLES EQUIPO']?.['Modelo']?.value 
                  || '';
                
                const isOlympusOrSLM = ['Olympus', 'SLM'].includes(modeloCarrozeria);
                const isLavacontenedores = modeloCarrozeria === 'Lavacontenedores';

                return (
                  <>
                    {isOlympusOrSLM && (
                      <Suspense fallback={null}>
                       <AccordionLoader accordionId="recolectores-olympus" onLoaded={handleAccordionLoaded}>
                        <RecolectoresOlympusAccordion
                          onImageIconClick={handleImageIconClick}
                          onSketchImageIconClick={handleSketchImageIconClick}
                          homologacion={homologacion}
                          onHomologacionChange={memoizedHomologacionChange}
                          editable={isEditable}
                        />
                     </AccordionLoader>
                      </Suspense>
                    )}

                    {isLavacontenedores && (
                      <Suspense fallback={null}>
                        <AccordionLoader accordionId="lavacontenedores" onLoaded={handleAccordionLoaded}>
                          <LavacontenedoresAccordion
                            onImageIconClick={handleImageIconClick}
                            onSketchImageIconClick={handleSketchImageIconClick}
                            homologacion={homologacion}
                            onHomologacionChange={memoizedHomologacionChange}
                            editable={isEditable}
                          />
                        </AccordionLoader>
                      </Suspense>
                    )}
                  </>
                );
              })()}

              <Suspense fallback={null}>
                <AccordionLoader accordionId="alumbrado-lateral" onLoaded={handleAccordionLoaded}>
                  <AlumbradoLateralAccordion
                    onImageIconClick={handleImageIconClick}
                    onSketchImageIconClick={handleSketchImageIconClick}
                    homologacion={homologacion}
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}
                  />
                </AccordionLoader>
              </Suspense>

              {/* Remaining accordions lazily grouped to reduce initial synchronous render cost */}
              <Suspense fallback={null}>
                <AccordionLoader accordionId="catadioptricos-laterales" onLoaded={handleAccordionLoaded}>
                  <CatadioptricosLateralesAccordion 
                    onImageIconClick={handleImageIconClick}
                    onSketchImageIconClick={handleSketchImageIconClick}
                    homologacion={homologacion}
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}
                  />
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="frenado-intermitente" onLoaded={handleAccordionLoaded}>
                  <FrenadoIntermitenteAccordion 
                    onImageIconClick={handleImageIconClick}
                    onSketchImageIconClick={handleSketchImageIconClick}
                    homologacion={homologacion}
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}
                  />
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="antiniebla" onLoaded={handleAccordionLoaded}>
                  <AntinieblaAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="marcha-atras" onLoaded={handleAccordionLoaded}>
                  <MarchaAtrasAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="catadioptricos-traseros" onLoaded={handleAccordionLoaded}>
                  <CatadioptricosTraserosAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="galibos-delanteros" onLoaded={handleAccordionLoaded}>
                  <GalibosDelanterosAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion}
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="tercera-luz-freno" onLoaded={handleAccordionLoaded}>
                  <TerceraLuzFrenoAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="bandas-reflectantes-laterales-traseras" onLoaded={handleAccordionLoaded}>
                  <BandasReflectantesLateralesTraserasAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="bandas-reflectantes-traseras-francia" onLoaded={handleAccordionLoaded}>
                  <BandasReflectantesTraserasFranciaAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="bandas-reflectantes-delanteras-francia" onLoaded={handleAccordionLoaded}>
                  <BandasReflectantesDelanterasFranciaAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="proteccion-lateral-derecho" onLoaded={handleAccordionLoaded}>
                  <ProteccionLateralDerechoAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="proteccion-lateral-izquierdo" onLoaded={handleAccordionLoaded}>
                  <ProteccionLateralIzquierdoAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="sistema-antiproyeccion" onLoaded={handleAccordionLoaded}>
                  <SistemaAntiproyeccionAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="sistema-antiempotramiento" onLoaded={handleAccordionLoaded}>
                  <SistemaAntiempotramientoAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="luz-matricula" onLoaded={handleAccordionLoaded}>
                  <LuzMatriculaAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="adhesivo-puntos-ciegos" onLoaded={handleAccordionLoaded}>
                  <AdhesivoPuntosCiegosAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="proteccion-delantera" onLoaded={handleAccordionLoaded}>
                  <ProteccionDelanteraAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="deposito-combustible-addblue" onLoaded={handleAccordionLoaded}>
                  <DepositoCombustibleAddblueAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="neumatico" onLoaded={handleAccordionLoaded}>
                  <NeumaticoAccordion 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="etiquetas-velocidad-francia" onLoaded={handleAccordionLoaded}>
                  <EtiquetasVelocidadFranciaAccordion 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="extintores-francia" onLoaded={handleAccordionLoaded}>
                  <ExtintoresFranciaAccordion 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="depositos-dimensiones" onLoaded={handleAccordionLoaded}>
                  <DepositosDimensionesAccordion 
                    onImageIconClick={handleImageIconClick} 
                    onSketchImageIconClick={handleSketchImageIconClick} 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange}
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="cem-francia" onLoaded={handleAccordionLoaded}>
                  <CEMFranciaAccordion 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="checks" onLoaded={handleAccordionLoaded}>
                  <ChecksAccordion 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>
              <Suspense fallback={null}>
                <AccordionLoader accordionId="resultado" onLoaded={handleAccordionLoaded}>
                  <ResultadoAccordion 
                    homologacion={homologacion} 
                    onHomologacionChange={memoizedHomologacionChange} 
                    editable={isEditable}/>
                </AccordionLoader>
              </Suspense>

            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button startIcon={<SendOutlined />} variant="outlined" color="primary" size="large" disabled={saving || generating  || !isEditable} onClick={() => handleGenerateAndSend()} >{t('capture.vehicle.generateAndSend')}</Button>
              <Button variant="contained" color="primary" size="large" onClick={() => handleNextStep('measurements')}>{t('capture.vehicle.next')}</Button>
            </Box>
          </Box>
        )}
      </DialogTitle>
      {error && (
        <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem', zIndex: (theme) => theme.zIndex.drawer + 200, position: 'sticky', bottom: 0, left: 0, right: 0 }}>
          {error}
          <IconButton onClick={() => setError(null)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </Alert>
      )}
      
      {galleryOpen && (
        <Suspense fallback={null}>
          <PhotoGallery
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            photos={selectedPhotos.length > 0 ? selectedPhotos : [helperPhotos[selectedHelper]]}
            initialPhotoType={selectedPhotos.length > 0 ? selectedPhotos[0]?.zone : helperPhotos[selectedHelper]?.zone}
            isMobile={isSmallScreen}
            mandatory={true}
          />
        </Suspense>
      )}
      {signatureDialogOpen && (
        <Suspense fallback={null}>
          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
          />
        </Suspense>
      )}
    </Dialog>
  );
};

export default withDevice(React.memo(Measurements));